import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, X, Check, ArrowLeft, ChevronRight,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SortableHeader, { usePagedSortableData, TablePager } from '../components/SortableTable';
import ResultCount from '../components/shared/ResultCount';
import { fetchJSON } from '../utils/api';
import { Field, inputCls, lockedInputCls, LockTip } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import SearchSelect from '../components/shared/SearchSelect';
import { STAFF_GRADES, REVIEWER_GRADE, MASTER_REVIEWER_DEPARTMENT } from '../utils/staffGrades';

// Categories grouped by the module they feed (a flat tab
// bar does not scale). `valueLabel` is the per-category header of the value
// column — "Value" told the user nothing once six lists shared one table.
const LIST_GROUPS = [
  {
    label: 'Engagement',
    items: [
      { key: 'engagement_status', label: 'Engagement Status', valueLabel: 'Status' },
      { key: 'service_provided', label: 'Service Provided', valueLabel: 'Service' },
      // Staff eligible as a QC6's engagement quality reviewer — the
      // QC6 form's EQR dropdown unions this roster with the active Audit
      // Partners. `kind: 'roster'` = a staff reference, not a typed value, so
      // this category reads and writes /api/settings/eqr-reviewers instead of
      // the lookup CRUD.
      {
        key: 'eqr_reviewers',
        label: 'Eligible EQR',
        kind: 'roster',
        noun: 'reviewer',
        newLabel: 'Add Staff',
        note: 'Serving Partners are eligible automatically — this list adds everyone else the QC6 form may name as EQR.',
      },
    ],
  },
  {
    label: 'Staff',
    items: [
      { key: 'staff_company', label: 'Staff Company', valueLabel: 'Company' },
      { key: 'staff_department', label: 'Staff Department', valueLabel: 'Department' },
      { key: 'staff_designation', label: 'Staff Designation', valueLabel: 'Designation' },
    ],
  },
  {
    // Shared list: the L&D Records → Qualification Types
    // form's Jurisdiction reads it today, anything else needing countries
    // reads the same one tomorrow — hence a group of its own rather than a
    // "Professional Qualification" heading.
    label: 'Shared',
    items: [
      { key: 'country', label: 'Country', valueLabel: 'Country' },
    ],
  },
  {
    // No L&D Session Category list here — the Sessions
    // form asks for a Category (L&D Setup → Training Categories).
    label: 'Professional Development',
    items: [
      // Why someone missed a training session. Both the
      // employee absence form and L&D Records → Absences pick from this list,
      // and the dashboard's breakdown counts it — so renaming an option here
      // rewrites the absences carrying it, and one still in use cannot be
      // deleted, only deactivated.
      { key: 'ld_absence_reason', label: 'Absence Reason', valueLabel: 'Reason' },
    ],
  },
];
const LIST_TABS = LIST_GROUPS.flatMap(g => g.items);

const THEAD_CLS = 'px-4 py-2 text-xs font-semibold text-slate-500 uppercase';

// staff_designation options carry a seniority grade (src/utils/staffGrades.js)
// that drives the engagement Partner / Director / Manager / EQR selects, the
// reviewer roster and the Official Training Report's bands.
const GRADE_CATEGORY = 'staff_designation';
const GRADE_OPTIONS = STAFF_GRADES;

// engagement_status carries five flags nothing else uses.
// Three say WHEN the status is applied automatically (each exactly one option),
// one says whether it belongs in the default view, one says it has no further
// steps:
//   isInitialStatus   — engagement CREATED (engreg approval and manual alike)
//   isDefault         — ANY engagement workflow first submitted
//   isCompletedStatus — QC35 APPROVED (the last gate in the pipeline)
//   showByDefault     — Overview's My engagements + Progress's default filter
//   isFinishedStatus  — Current Stage reads Finished
const DEFAULT_CATEGORY = 'engagement_status';

// service_provided options carry one flag: allowMultiplePerFye — a company
// may hold several engagements of the service for one FYE; from the second
// on, each carries a Description of Service of its own and is numbered.
const MULTI_FYE_CATEGORY = 'service_provided';

// Controls the server would refuse (409) are locked up front and say why on
// hover. lockOf returns a reason per control — value, active, grade, remove —
// or null where the control is free; Sort Order is never locked.
//   - The QM department feeds the reviewer list of the client and engagement
//     workflows by its name (REVIEWER_SOURCE).
//   - The last active Partner-grade / Director-grade designation feeds the
//     reviewer lists (REVIEWER_SOURCE).
//   - A status carrying an automatic mark (created / work starts / QC35
//     approved) cannot be deleted (FLAG_IN_USE).
const SYSTEM_LOCK_HINT = 'System default — cannot be changed';
const NO_LOCK = { value: null, active: null, grade: null, remove: null };
const lockOf = (category, option, rows) => {
  if (!option) return NO_LOCK;
  if (category === 'staff_department' && option.value === MASTER_REVIEWER_DEPARTMENT) {
    return { value: SYSTEM_LOCK_HINT, active: SYSTEM_LOCK_HINT, grade: null, remove: SYSTEM_LOCK_HINT };
  }
  if (category === GRADE_CATEGORY && option.active
      && Object.values(REVIEWER_GRADE).includes(option.grade)
      && !rows.some((o) => o.id !== option.id && o.active && o.grade === option.grade)) {
    const why = `Last active ${option.grade}-grade designation — the reviewer lists need at least one`;
    return { value: null, active: why, grade: why, remove: why };
  }
  if (category === DEFAULT_CATEGORY && (option.isInitialStatus || option.isDefault || option.isCompletedStatus)) {
    return { ...NO_LOCK, remove: 'Applied automatically — move that mark to another status before deleting' };
  }
  return NO_LOCK;
};

const SettingsLists = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('settings:lists:edit');

  const [activeTab, setActiveTab] = useState('engagement_status');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const { rows: pageRows, sortConfig, requestSort, page, setPage, total } = usePagedSortableData(rows);

  const isRoster = LIST_TABS.find(t => t.key === activeTab)?.kind === 'roster';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (LIST_TABS.find(t => t.key === activeTab)?.kind === 'roster') {
        const data = await fetchJSON('/api/settings/eqr-reviewers');
        setRows(data.reviewers || []);
      } else {
        const data = await fetchJSON(`/api/settings/lookups/${activeTab}`);
        setRows(data.options || []);
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [activeTab]);

  useEffect(() => {
    if (!canEdit) return;
    load();
  }, [canEdit, load]);

  if (!canEdit) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-red-800">Editor access required</h2>
          <p className="text-sm text-red-600 mt-1">You need master data editing rights to access this area.</p>
        </div>
      </div>
    );
  }

  const activeItem = LIST_TABS.find(t => t.key === activeTab);
  const activeGroup = LIST_GROUPS.find(g => g.items.some(t => t.key === activeTab));
  const tabLabel = activeItem?.label || '';
  const valueLabel = activeItem?.valueLabel || 'Value';
  const showGrade = activeTab === GRADE_CATEGORY;
  const showDefault = activeTab === DEFAULT_CATEGORY;
  const showMultiFye = activeTab === MULTI_FYE_CATEGORY;
  const colCount = isRoster ? 7 : 4 + (showGrade ? 1 : 0) + (showDefault ? 3 : 0) + (showMultiFye ? 1 : 0);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <Link to="/manage" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4" />Management</Link>
      <div className="mt-2 mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Dropdown Lists</h1>
          <p className="text-sm text-slate-500 mt-1">Maintain the dropdown options used by the engagement, staff and L&amp;D forms.</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> {activeItem?.newLabel || 'New Option'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-4">{error}</div>
      )}

      {/* Grouped sidebar + table (a flat tab bar does not scale
          as categories accumulate) */}
      <div className="flex flex-col sm:flex-row gap-4 items-start">
        {/* Category picker: group captions are tinted, non-interactive bands;
            options are rows carrying a left accent bar and a chevron. */}
        <nav aria-label="List categories" className="bg-white rounded-lg border border-slate-200 w-full sm:w-60 shrink-0 overflow-hidden divide-y divide-slate-200">
          {LIST_GROUPS.map(group => (
            <div key={group.label}>
              <p className="px-3 py-1.5 bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-500">{group.label}</p>
              <div className="p-1.5 space-y-0.5">
                {group.items.map(t => {
                  const active = activeTab === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setActiveTab(t.key)}
                      aria-current={active ? 'page' : undefined}
                      className={`group w-full flex items-center gap-2 pl-2.5 pr-2 py-2 rounded-md border-l-2 text-sm text-left transition-colors ${active ? 'border-blue-600 bg-blue-50 text-blue-700 font-semibold' : 'border-transparent text-slate-700 font-medium hover:bg-slate-100 hover:border-slate-300'}`}
                    >
                      <span className="flex-1 min-w-0 truncate">{t.label}</span>
                      <ChevronRight className={`w-4 h-4 shrink-0 ${active ? 'text-blue-500' : 'text-slate-300 group-hover:text-slate-500'}`} />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="flex-1 min-w-0 w-full">
          <div className="flex items-center justify-between mb-2 px-1">
            <h2 className="text-sm font-semibold text-slate-700">
              {activeGroup && <span className="font-normal text-slate-400">{activeGroup.label} / </span>}
              {tabLabel}
            </h2>
            <ResultCount count={rows.length} noun={activeItem?.noun || 'option'} loading={loading} className="shrink-0" />
          </div>

          {activeItem?.note && (
            <p className="text-sm text-slate-500 mb-2 px-1">{activeItem.note}</p>
          )}

          {/* Table */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              {isRoster ? (
                <tr>
                  <SortableHeader label="Staff" sortKey="staffName" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                  <SortableHeader label="Designation" sortKey="designation" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                  <SortableHeader label="Department" sortKey="department" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                  <SortableHeader label="Employment" sortKey="employment" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                  <SortableHeader label="Remarks" sortKey="remarks" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                  <SortableHeader label="Status" sortKey="active" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                  <SortableHeader label="Actions" sortable={false} align="right" className={THEAD_CLS} />
                </tr>
              ) : (
              <tr>
                <SortableHeader label={valueLabel} sortKey="value" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase" />
                {showGrade && <SortableHeader label="Grade" sortKey="grade" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase" />}
                <SortableHeader label="Sort Order" sortKey="sortOrder" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase" />
                {showDefault && <SortableHeader label="Applied When" sortKey="isDefault" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase" />}
                {showDefault && <SortableHeader label="Default View" sortKey="showByDefault" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase" />}
                {showDefault && <SortableHeader label="Finished" sortKey="isFinishedStatus" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase" />}
                {showMultiFye && <SortableHeader label="Multiple per FYE" sortKey="allowMultiplePerFye" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase" />}
                <SortableHeader label="Status" sortKey="active" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase" />
                <SortableHeader label="Actions" sortable={false} align="right" className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase" />
              </tr>
              )}
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={colCount} className="px-4 py-8 text-center text-slate-500">Loading…</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={colCount} className="px-4 py-8 text-center text-slate-500">
                    {isRoster ? 'No one on the Eligible EQR list yet — only serving Partners can be named as EQR.' : 'No options found'}
                  </td>
                </tr>
              ) : isRoster ? pageRows.map(r => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium text-slate-900">{r.staffName}</td>
                  <td className="px-4 py-2 text-slate-700">{r.designation || '—'}</td>
                  <td className="px-4 py-2 text-slate-700">{r.department || '—'}</td>
                  {/* Employment is the staff master's own standing, not
                      eligibility: whoever has left the firm drops off the QC6
                      dropdown whatever the Status column says. */}
                  <td className="px-4 py-2">
                    {r.employment === 'Resigned' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">Resigned</span>
                    ) : (
                      <span className="text-xs text-slate-500">In service</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-600">{r.remarks || '—'}</td>
                  <td className="px-4 py-2">
                    {r.active ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700">Active</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => setEditing(r)}
                        title="Edit"
                        className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleting(r)}
                        title="Remove from list"
                        className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : pageRows.map(r => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-900">{r.value}</td>
                  {showGrade && (
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                        {r.grade || '—'}
                      </span>
                    </td>
                  )}
                  <td className="px-4 py-2 text-slate-700">{r.sortOrder}</td>
                  {showDefault && (
                    <td className="px-4 py-2">
                      {r.isInitialStatus && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-violet-50 text-violet-700 whitespace-nowrap">
                          On creation
                        </span>
                      )}
                      {r.isDefault && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 whitespace-nowrap">
                          On first submission
                        </span>
                      )}
                      {r.isCompletedStatus && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 whitespace-nowrap">
                          On QC35 Form approval
                        </span>
                      )}
                    </td>
                  )}
                  {showDefault && (
                    <td className="px-4 py-2">
                      {r.showByDefault && <Check className="w-4 h-4 text-green-600" />}
                    </td>
                  )}
                  {showDefault && (
                    <td className="px-4 py-2">
                      {r.isFinishedStatus && <Check className="w-4 h-4 text-green-600" />}
                    </td>
                  )}
                  {showMultiFye && (
                    <td className="px-4 py-2">
                      {r.allowMultiplePerFye && <Check className="w-4 h-4 text-green-600" />}
                    </td>
                  )}
                  <td className="px-4 py-2">
                    {r.active ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700">Active</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => setEditing(r)}
                        title="Edit"
                        className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {lockOf(activeTab, r, rows).remove ? (
                        <LockTip text={lockOf(activeTab, r, rows).remove} align="right">
                          <span className="p-1.5 text-slate-300">
                            <Trash2 className="w-4 h-4" />
                          </span>
                        </LockTip>
                      ) : (
                        <button
                          onClick={() => setDeleting(r)}
                          title="Delete"
                          className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePager
          page={page}
          setPage={setPage}
          total={total}
          className="flex items-center justify-between px-4 py-2 border-t border-slate-200 bg-slate-50"
        />
          </div>
        </div>
      </div>

      {(showCreate || editing) && (isRoster ? (
        <ReviewerModal
          reviewer={editing}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={() => { setShowCreate(false); setEditing(null); load(); }}
        />
      ) : (
        <OptionModal
          option={editing}
          lock={lockOf(activeTab, editing, rows)}
          category={activeTab}
          categoryLabel={tabLabel}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={() => { setShowCreate(false); setEditing(null); load(); }}
        />
      ))}
      {deleting && (
        <DeleteOptionModal
          option={deleting}
          category={activeTab}
          roster={isRoster}
          onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); load(); }}
        />
      )}
    </div>
  );
};

const OptionModal = ({ option, lock = NO_LOCK, category, categoryLabel, onClose, onSaved }) => {
  const isEdit = !!option;
  const showGrade = category === GRADE_CATEGORY;
  const showDefault = category === DEFAULT_CATEGORY;
  const showMultiFye = category === MULTI_FYE_CATEGORY;
  const [form, setForm] = useState({
    value: option?.value || '',
    sortOrder: option?.sortOrder != null ? String(option.sortOrder) : '',
    active: option ? !!option.active : true,
    grade: option?.grade || '',
    isDefault: option ? !!option.isDefault : false,
    isInitialStatus: option ? !!option.isInitialStatus : false,
    isCompletedStatus: option ? !!option.isCompletedStatus : false,
    showByDefault: option ? !!option.showByDefault : false,
    isFinishedStatus: option ? !!option.isFinishedStatus : false,
    allowMultiplePerFye: option ? !!option.allowMultiplePerFye : false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Re-grading a designation moves every holder at once, and reviewer standing
  // is re-read at the signature — so one edit can leave a batch of in-flight
  // requests that nobody is able to approve. The requests are shown before
  // saving; the edit itself is never blocked. { designation, staff, requests }.
  const [standingWarning, setStandingWarning] = useState(null);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // 空白的必填格：按 Save 後就地標紅並列進橫幅，填好就消。
  const gaps = useMemo(() => {
    const g = [];
    if (!form.value.trim()) g.push({ key: 'value', label: 'Value' });
    if (showGrade && !form.grade) g.push({ key: 'grade', label: 'Grade' });
    return g;
  }, [form, showGrade]);

  // The only field that changes reviewer standing.
  const gradeChanged = isEdit && showGrade && form.grade !== (option.grade || '');
  const validation = useFormValidation(gaps);

  const save = async () => {
    if (!validation.validate()) { setError(null); return; }
    if (gradeChanged && !standingWarning) {
      try {
        const open = await fetchJSON(`/api/settings/lookups/${category}/${option.id}/open-reviews`);
        if ((open.requests || []).length > 0) {
          setStandingWarning(open);
          return;
        }
      } catch {
        // Information, not a gate: if the look-up fails the edit goes through.
      }
    }

    setSaving(true);
    setError(null);
    const body = { value: form.value.trim() };
    if (form.sortOrder !== '') body.sortOrder = Number(form.sortOrder);
    if (showGrade) body.grade = form.grade;
    if (showMultiFye) body.allowMultiplePerFye = form.allowMultiplePerFye;
    if (isEdit) body.active = form.active;
    if (isEdit && showDefault) {
      body.isDefault = form.isDefault;
      body.isInitialStatus = form.isInitialStatus;
      body.isCompletedStatus = form.isCompletedStatus;
      body.showByDefault = form.showByDefault;
      body.isFinishedStatus = form.isFinishedStatus;
    }
    try {
      if (isEdit) {
        await fetchJSON(`/api/settings/lookups/${category}/${option.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      } else {
        await fetchJSON(`/api/settings/lookups/${category}`, {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }
      onSaved();
    } catch (e) {
      if (e.code === 'DUPLICATE_VALUE') {
        setError('This value already exists in the list.');
      } else {
        setError(e.message);
      }
    }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">
            {isEdit ? `Edit ${option.value}` : `New ${categoryLabel} Option`}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <FormValidationProvider value={validation.providerValue}>
          <Field label="Value" required field="value">
            {lock.value ? (
              <LockTip text={lock.value} block>
                <input type="text" value={form.value} disabled className={lockedInputCls} />
              </LockTip>
            ) : (
              <input type="text" value={form.value} onChange={e => setField('value', e.target.value)} className={inputCls} />
            )}
          </Field>
          {showGrade && (
            <Field label="Grade" required field="grade" hint="Drives the engagement Partner / Director / Manager / EQR dropdowns, the Partner / Director reviewer lists and the Official Training Report's grade bands.">
              {lock.grade ? (
                <LockTip text={lock.grade} block>
                  <select value={form.grade} disabled className={lockedInputCls}>
                    {GRADE_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </LockTip>
              ) : (
                <select value={form.grade} onChange={e => setField('grade', e.target.value)} className={inputCls}>
                  <option value="">Select…</option>
                  {GRADE_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              )}
            </Field>
          )}
          <Field label="Sort Order" hint="Lower numbers appear first in dropdowns.">
            <input type="number" value={form.sortOrder} onChange={e => setField('sortOrder', e.target.value)} className={inputCls} />
          </Field>
          {isEdit && (
            lock.active ? (
              <LockTip text={lock.active}>
                <span className="flex items-center gap-2 text-sm text-slate-400">
                  <input type="checkbox" checked={form.active} disabled />
                  Active
                </span>
              </LockTip>
            ) : (
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.active} onChange={e => setField('active', e.target.checked)} />
                Active
              </label>
            )
          )}
          {showMultiFye && (
            <div className="pt-1 border-t border-slate-100">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.allowMultiplePerFye} onChange={e => setField('allowMultiplePerFye', e.target.checked)} />
                Allow several engagements per company and FYE
              </label>
              <p className="text-xs text-slate-500 mt-1">Ticked: a company may hold several engagements of this service for the same FYE. From the second one on, each carries a Description of Service of its own and is numbered (#2, #3…) in its name so they can be told apart. Unticked: one engagement of this service per company and FYE.</p>
            </div>
          )}
          {isEdit && showDefault && (
            <div className="space-y-3 pt-1 border-t border-slate-100">
              <div>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={form.isInitialStatus} onChange={e => setField('isInitialStatus', e.target.checked)} />
                  Apply when an engagement is created
                </label>
                <p className="text-xs text-slate-500 mt-1">Used by both Engagement Registration approval and manual creation — neither asks the user to pick a status. Only one status can hold this.</p>
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={form.isDefault} onChange={e => setField('isDefault', e.target.checked)} />
                  Apply when the first workflow is submitted
                </label>
                <p className="text-xs text-slate-500 mt-1">Applied the moment QC6 / QC7 Forms, CaseWare, Charge Code &amp; Team Update, Signed Financial Statements or QC35 Form is first submitted for this engagement. Only one status can hold this.</p>
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={form.isCompletedStatus} onChange={e => setField('isCompletedStatus', e.target.checked)} />
                  Apply when QC35 Form is approved
                </label>
                <p className="text-xs text-slate-500 mt-1">QC35 Form is the last gate in the pipeline — approving it means the engagement is finished. Only one status can hold this.</p>
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={form.showByDefault} onChange={e => setField('showByDefault', e.target.checked)} />
                  Show in the default view
                </label>
                <p className="text-xs text-slate-500 mt-1">Engagements on this status appear in Overview&rsquo;s My engagements, and in Engagement Progress before any filter is changed.</p>
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={form.isFinishedStatus} onChange={e => setField('isFinishedStatus', e.target.checked)} />
                  Finished (no further steps)
                </label>
                <p className="text-xs text-slate-500 mt-1">Engagements on this status show Finished as their Current Stage, whatever stages remain. On any other status, Current Stage shows the next outstanding stage, and Finished only once every stage is done.</p>
              </div>
            </div>
          )}
          {(error || validation.error) && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <span>{error || validation.error}</span>
            </div>
          )}
          </FormValidationProvider>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
            <button onClick={save} disabled={saving} className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
      {standingWarning && (
        <div className="fixed inset-0 bg-slate-900/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Requests Awaiting These Reviewers</h3>
              <button onClick={() => setStandingWarning(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-700">
                Changing the grade of <span className="font-semibold">{standingWarning.designation}</span> moves
                everyone holding it ({standingWarning.staff.length}
                {' '}staff). {standingWarning.requests.length}{' '}
                request{standingWarning.requests.length === 1 ? '' : 's'} still under review
                name{standingWarning.requests.length === 1 ? 's' : ''} one of them as reviewer.
              </p>
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
                {standingWarning.requests.map(r => (
                  <li key={`${r.flow}-${r.id}`} className="px-3 py-2 text-sm">
                    <span className="font-medium text-slate-800">{r.requestNo}</span>
                    <span className="text-slate-500"> · {r.label}</span>
                    <span className="block text-xs text-slate-500">
                      {r.reviewerName || '—'} · requested by {r.requesterEmail}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                <span>
                  This change is saved either way. Where the new grade no longer meets what a
                  stage requires, that reviewer can no longer approve and the request shows up
                  in its requester&apos;s list as needing a new reviewer. No email is sent.
                </span>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setStandingWarning(null)} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
                <button onClick={save} disabled={saving} className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save Anyway'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Eligible EQR is a staff roster, so New picks a person instead of typing a
 * name; Edit keeps the person fixed (remove the row and add the right one) and
 * only carries the remarks and the on/off switch.
 */
const ReviewerModal = ({ reviewer, onClose, onSaved }) => {
  const isEdit = !!reviewer;
  const [staffOptions, setStaffOptions] = useState([]);
  const [form, setForm] = useState({
    staffId: reviewer?.staffId ? String(reviewer.staffId) : '',
    remarks: reviewer?.remarks || '',
    active: reviewer ? !!reviewer.active : true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEdit) return;
    fetchJSON('/api/settings/eqr-reviewers/staff-options')
      .then(data => setStaffOptions((data.staff || []).filter(s => !s.onRoster)))
      .catch(e => setError(e.message));
  }, [isEdit]);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // 空白的必填格：按 Save 後就地標紅並列進橫幅，填好就消（編輯時人是固定的）。
  const gaps = useMemo(() => (!isEdit && !form.staffId ? [{ key: 'staffId', label: 'Staff' }] : []), [isEdit, form.staffId]);
  const validation = useFormValidation(gaps);

  const save = async () => {
    if (!validation.validate()) { setError(null); return; }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await fetchJSON(`/api/settings/eqr-reviewers/${reviewer.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ remarks: form.remarks.trim(), active: form.active }),
        });
      } else {
        await fetchJSON('/api/settings/eqr-reviewers', {
          method: 'POST',
          body: JSON.stringify({ staffId: Number(form.staffId), remarks: form.remarks.trim() }),
        });
      }
      onSaved();
    } catch (e) {
      if (e.code === 'DUPLICATE_VALUE') {
        setError('This staff member is already on the Eligible EQR list — edit that row instead.');
      } else {
        setError(e.message);
      }
    }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">
            {isEdit ? `Edit ${reviewer.staffName}` : 'Add an Eligible EQR'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <FormValidationProvider value={validation.providerValue}>
          <Field label="Staff" required field="staffId" hint={isEdit ? undefined : 'Serving staff only, and only those not already on the list.'}>
            {isEdit ? (
              <p className="text-sm text-slate-800 py-2">
                {reviewer.staffName}
                <span className="text-slate-500"> · {reviewer.designation || 'No designation'}</span>
              </p>
            ) : (
              <SearchSelect
                value={form.staffId}
                onChange={v => setField('staffId', v)}
                options={staffOptions.map(s => ({ value: s.id, label: s.name, group: s.designation || 'No designation' }))}
                placeholder="Search the staff list…"
              />
            )}
          </Field>
          <Field label="Remarks">
            <textarea
              rows={3}
              value={form.remarks}
              onChange={e => setField('remarks', e.target.value)}
              className={inputCls}
            />
          </Field>
          {isEdit && (
            <div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.active} onChange={e => setField('active', e.target.checked)} />
                Active
              </label>
              <p className="text-xs text-slate-500 mt-1">Switching this off keeps the row but drops the person from the QC6 EQR dropdown.</p>
            </div>
          )}
          {(error || validation.error) && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <span>{error || validation.error}</span>
            </div>
          )}
          </FormValidationProvider>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
            <button onClick={save} disabled={saving} className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeleteOptionModal = ({ option, category, roster = false, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [inUse, setInUse] = useState(false);
  const name = roster ? option.staffName : option.value;

  const confirmDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await fetchJSON(
        roster
          ? `/api/settings/eqr-reviewers/${option.id}`
          : `/api/settings/lookups/${category}/${option.id}`,
        { method: 'DELETE' }
      );
      onDeleted();
    } catch (e) {
      if (e.code === 'IN_USE') setInUse(true);
      setError(e.message);
    }
    finally { setDeleting(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">{roster ? 'Remove from the List' : 'Delete Option'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700">
            {roster ? (
              <>
                Take <span className="font-semibold">{name}</span> off the Eligible EQR list? They stay on the staff master and
                the QC6 forms naming them are untouched — to pause them instead, switch Active off.
              </>
            ) : (
              <>
                Are you sure you want to delete <span className="font-semibold">{name}</span>? This action cannot be undone.
                Options that are still in use cannot be deleted — deactivate them instead.
              </>
            )}
          </p>
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <span>{error}</span>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">
              {inUse ? 'Close' : 'Cancel'}
            </button>
            {!inUse && (
              <button onClick={confirmDelete} disabled={deleting} className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50">
                {deleting ? 'Removing…' : (roster ? 'Remove' : 'Delete')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsLists;
